import { getDate, getDaysInMonth, isSameMonth, parseISO } from "date-fns";
import { CircleCheck, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useCaptainBillingUsage } from "../../api/useCaptainBillingUsage";
import { CAPTAIN_USAGE_MONTHS } from "../../constants";
import type { CaptainBillingAccount } from "../../types";
import { balanceRunOut, capResetDate } from "../../utils";

import { Card } from "@/components/ui/v2/card";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { cn } from "@/lib/utils/cn";
import { formatDayMonth, formatMonthYear } from "@/utils/dateUtils";
import { useValueFormatter } from "@/utils/useValueFormatter";

export function MonthSoFar({ account }: { account: CaptainBillingAccount }) {
  const { t, i18n } = useTranslation();
  const { format } = useValueFormatter();
  const { data, isLoading, isError } = useCaptainBillingUsage(account.clientId, CAPTAIN_USAGE_MONTHS);
  const monthStart = parseISO(account.month);
  const now = new Date();
  const thisMonth = data?.find((month) => isSameMonth(parseISO(month.month), monthStart));
  const used = (thisMonth?.usedFromCapTokens ?? 0) + (thisMonth?.usedFromTopUpTokens ?? 0);
  const replies = thisMonth?.replies ?? 0;
  const runOut = balanceRunOut(account, used, now);
  const formatTokens = (tokens: number) => format(tokens, "decimal");
  const isCurrent = isSameMonth(monthStart, now);

  return (
    <Card padding="md" className="h-full gap-4 px-5 py-5" data-testid="captain-month-so-far">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-lg font-semibold text-foreground">
          {t("captainBilling.monthSoFar.title", { month: formatMonthYear(monthStart, i18n.language, "MMMM") })}
        </h2>
        {isCurrent && (
          <span className="text-sm text-muted-foreground tabular-nums">
            {t("captainBilling.monthSoFar.day", { day: getDate(now), days: getDaysInMonth(monthStart) })}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-5 w-full" />
          ))}
        </div>
      ) : (
        <dl className="flex flex-1 flex-col divide-y divide-border">
          <Row label={t("captainBilling.monthSoFar.tokensUsed")}>{isError ? "—" : formatTokens(used)}</Row>
          {account.monthlyCapTokens > 0 && (
            <Row label={t("captainBilling.monthSoFar.fromCap")}>
              {isError ? "—" : formatTokens(thisMonth?.usedFromCapTokens ?? 0)}
            </Row>
          )}
          <Row label={t("captainBilling.monthSoFar.fromTopUp")}>
            {isError ? "—" : formatTokens(thisMonth?.usedFromTopUpTokens ?? 0)}
          </Row>
          <Row label={t("captainBilling.monthSoFar.replies")}>{isError ? "—" : format(replies, "whole")}</Row>
          <Row label={t("captainBilling.monthSoFar.perReply")}>
            {isError || replies === 0
              ? "—"
              : t("captainBilling.monthSoFar.perReplyValue", { tokens: format(used / replies, "decimal") })}
          </Row>
          {account.monthlyCapTokens > 0 && (
            <Row label={t("captainBilling.monthSoFar.refillsOn")}>
              {formatDayMonth(capResetDate(account.month), i18n.language)}
            </Row>
          )}
        </dl>
      )}
      {runOut && <Pace runOut={runOut} />}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-between gap-4 py-2.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-semibold text-foreground tabular-nums">{children}</dd>
    </div>
  );
}

function Pace({ runOut }: { runOut: Date | "lasts" }) {
  const { t, i18n } = useTranslation();
  const lasts = runOut === "lasts";
  return (
    <p
      data-testid="captain-month-pace"
      className={cn(
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium",
        lasts
          ? "border-(--badge-success-border) bg-(--badge-success-bg) text-(--badge-success-text)"
          : "border-(--badge-warning-border) bg-(--badge-warning-bg) text-(--badge-warning-text)",
      )}
    >
      {lasts ? <CircleCheck className="size-4 shrink-0" aria-hidden /> : <TriangleAlert className="size-4 shrink-0" aria-hidden />}
      {lasts
        ? t("captainBilling.monthSoFar.pace.lasts")
        : t("captainBilling.monthSoFar.pace.runsOut", { date: formatDayMonth(runOut, i18n.language) })}
    </p>
  );
}
